import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Typography, Button, CircularProgress, Box, Paper, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

// Use react-pdf for PDF text extraction (but not rendering)
import { Document } from 'react-pdf';

// Import PDF.js directly and configure worker
import * as pdfjs from 'pdfjs-dist';

// Import translation service
import { translateText } from '../services/translationService';

// Configure worker - use the simplest possible setup
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

console.log('🔍 [DEBUG] EnhancedPdfReader using PDF.js version:', pdfjs.version);

const EnhancedPdfReader = ({ pdfUrl, onTextSelect, onPageChange, currentPage, totalPages }) => {
  console.log('🔍 [DEBUG] EnhancedPdfReader mounted - pdfUrl:', pdfUrl ? 'SET' : 'NOT SET');
  
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(currentPage || 1);
  const [isLoading, setIsLoading] = useState(true);
  const [allPagesText, setAllPagesText] = useState([]);
  const [translation, setTranslation] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [popupPlacement, setPopupPlacement] = useState('above');
  const [isTranslating, setIsTranslating] = useState(false);
  const mouseDownTimeRef = useRef(0);
  const isDraggingRef = useRef(false);
  const [pdfTextFontSize, setPdfTextFontSize] = useState(16);
  const [highlightedWordIndices, setHighlightedWordIndices] = useState(new Set());

  const lastClickedWordRef = useRef(null);
  const textRef = useRef(null);

  // Sync with external page changes from navigator
  useEffect(() => {
    if (currentPage && currentPage !== pageNumber && currentPage > 0) {
      setPageNumber(currentPage);
    }
  }, [currentPage, pageNumber]);

  // Keyboard arrow navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); previousPage(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); nextPage(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Page navigation functions
  const previousPage = () => {
    clearTranslations(); // Hide popup on navigation
    window.speechSynthesis.cancel();
    const newPage = Math.max(1, pageNumber - 1);
    setPageNumber(newPage);
    onPageChange && onPageChange(newPage);
  };

  const handleZoomOut = () => {
    setPdfTextFontSize(prev => Math.max(12, prev - 2));
  };

  const handleZoomIn = () => {
    setPdfTextFontSize(prev => Math.min(28, prev + 2));
  };

  const nextPage = () => {
    clearTranslations(); // Hide popup on navigation
    window.speechSynthesis.cancel();
    const newPage = Math.min(numPages || 1, pageNumber + 1);
    setPageNumber(newPage);
    onPageChange && onPageChange(newPage);
  };

  // Ultra-simple options - minimal configuration
  const options = useMemo(() => ({
    disableWorker: true,
    disableStream: true,
    disableRange: true,
  }), []); 

  console.log('🔍 [DEBUG] PDF reader options:', options);

  // Function to handle when the document loads successfully
  const onDocumentLoadSuccess = useCallback((pdf) => {
    console.log('🎉 [SUCCESS] PDF loaded successfully! Pages:', pdf.numPages);
    
    setNumPages(pdf.numPages);
    setIsLoading(false);
    
    // Notify parent of total pages
    if (onPageChange) {
      onPageChange(pageNumber, pdf.numPages);
    }
    
    // Extract text from all pages
    const extractAllText = async () => {
      console.log('🔍 [DEBUG] Starting text extraction...');
      const textArray = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        try {
          console.log(`🔍 [DEBUG] Extracting page ${i}...`);
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const text = textContent.items.map(item => item.str).join(' ');
          textArray.push(text || 'No text found on this page. Go to the next.');
          console.log(`✅ [SUCCESS] Page ${i} extracted:`, text.substring(0, 50) + '...');
        } catch (error) {
          console.error(`❌ [ERROR] Failed to extract page ${i}:`, error);
          textArray.push('Error extracting text from this page');
        }
      }
      
      setAllPagesText(textArray);
      console.log('🎉 [SUCCESS] All text extraction complete!');
    };
    
    extractAllText();
  }, [pageNumber, onPageChange]);

  // Function to handle document load errors
  const onDocumentLoadError = useCallback((error) => {
    console.error('🚨 [ERROR] Failed to load PDF:', error);
    setIsLoading(false);
    setAllPagesText(['ERROR: Failed to load PDF document. ' + error.message]);
  }, []);

  // Translation functions
  const handleWordClick = async (word, index, event) => {
    if (isDraggingRef.current || Date.now() - mouseDownTimeRef.current > 300) return;
    
    event.stopPropagation();

    if (translation && lastClickedWordRef.current === word) {
      clearTranslations();
      return;
    }

    setHighlightedWordIndices(new Set([index]));

    const rect = event.target.getBoundingClientRect();
    const position = calculatePosition(rect);
    setPopupPosition({ x: position.x, y: position.y });
    setPopupPlacement(position.placement);
    setIsTranslating(true);

    try {
      const sourceLanguage = localStorage.getItem('sourceLang') || localStorage.getItem('sourceLanguage') || 'auto';
      const targetLanguage = localStorage.getItem('targetLang') || localStorage.getItem('targetLanguage') || 'en';
      
      const translationResult = await translateText(word, sourceLanguage, targetLanguage, {
        trigger: 'click',
        contentType: 'pdf',
      });
      
      // Handle both string and object responses from translation service
      const translatedText = typeof translationResult === 'string' 
        ? translationResult 
        : translationResult.translatedText || translationResult.text || 'Translation unavailable';
      
      setTranslation(translatedText);
      lastClickedWordRef.current = word;
      
      if (onTextSelect) {
        onTextSelect(word, translatedText);
      }
    } catch (error) {
      console.error('Translation error:', error);
      setTranslation('Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTextSelection = async () => {
    const selection = window.getSelection();
    const rawText = selection?.toString() || '';
    const selText = rawText.replace(/\s+/g, ' ').trim();
    if (!selText) return;

    // --- Compute highlight indices from word spans ---
    let selectionHighlightIndices = null;
    let spans = [];
    try {
      if (selection && selection.rangeCount > 0 && textRef.current) {
        const range = selection.getRangeAt(0);
        spans = Array.from(textRef.current.querySelectorAll('.word'));

        const getWordEl = (node) => {
          if (!node) return null;
          const el = node.nodeType === 1 ? node : node.parentElement;
          return el?.closest ? el.closest('.word') : null;
        };

        const startEl = getWordEl(range.startContainer);
        const endEl = getWordEl(range.endContainer);

        if (startEl && endEl) {
          const startIdx = Number(startEl.dataset.wordIdx);
          const endIdx = Number(endEl.dataset.wordIdx);
          if (!Number.isNaN(startIdx) && !Number.isNaN(endIdx)) {
            const minIdx = Math.min(startIdx, endIdx);
            const maxIdx = Math.max(startIdx, endIdx);
            const indices = spans
              .map((el) => Number(el.dataset.wordIdx))
              .filter((n) => !Number.isNaN(n) && n >= minIdx && n <= maxIdx);
            if (indices.length > 0) selectionHighlightIndices = new Set(indices);
          }
        }

        // Fallback: intersectsNode
        if (!selectionHighlightIndices) {
          const indices = [];
          spans.forEach((el) => {
            if (range.intersectsNode(el)) {
              const idx = el.dataset.wordIdx;
              if (idx !== undefined) {
                const n = Number(idx);
                if (!Number.isNaN(n)) indices.push(n);
              }
            }
          });
          if (indices.length > 0) selectionHighlightIndices = new Set(indices);
        }
      }
    } catch (_) {
      selectionHighlightIndices = null;
    }

    if (!selectionHighlightIndices || selectionHighlightIndices.size === 0) {
      return; // couldn't resolve any word spans
    }

    // --- Word-boundary expansion: build full text from highlighted word spans ---
    let text = selText;
    try {
      const highlightedSpans = spans
        .filter((el) => selectionHighlightIndices.has(Number(el.dataset.wordIdx)))
        .sort((a, b) => Number(a.dataset.wordIdx) - Number(b.dataset.wordIdx));
      if (highlightedSpans.length > 0) {
        text = highlightedSpans.map((el) => el.textContent.trim()).join(' ');
      }
    } catch (_) { /* keep selText */ }

    const wordsCount = text.split(/\s+/).filter(Boolean).length;
    const maxSelectableWords = 20;
    if (wordsCount > maxSelectableWords) {
      setTranslation(`Please select up to ${maxSelectableWords} words to translate at a time.`);
      setHighlightedWordIndices(new Set());
      lastClickedWordRef.current = null;
      return;
    }

    // --- Position popup BEFORE clearing the browser selection ---
    let rect = null;
    try {
      rect = selection?.rangeCount ? selection.getRangeAt(0).getBoundingClientRect() : null;
    } catch (_) { rect = null; }

    if (rect && rect.width > 0) {
      const position = calculatePosition(rect);
      setPopupPosition({ x: position.x, y: position.y });
      setPopupPlacement(position.placement);
    }

    // Set highlights and clear browser selection to reveal styled boxes
    setHighlightedWordIndices(selectionHighlightIndices);
    lastClickedWordRef.current = text;
    window.getSelection()?.removeAllRanges();

    // --- Translate ---
    setIsTranslating(true);
    try {
      const sourceLanguage = localStorage.getItem('sourceLang') || localStorage.getItem('sourceLanguage') || 'auto';
      const targetLanguage = localStorage.getItem('targetLang') || localStorage.getItem('targetLanguage') || 'en';

      const translationResult = await translateText(text, sourceLanguage, targetLanguage, {
        trigger: 'selection',
        contentType: 'pdf',
      });

      const translatedText = typeof translationResult === 'string'
        ? translationResult
        : translationResult.translatedText || translationResult.text || 'Translation unavailable';

      setTranslation(translatedText);

      if (onTextSelect) {
        onTextSelect(text, translatedText);
      }
    } catch (error) {
      console.error('Translation error:', error);
      setTranslation('Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const calculatePosition = (rect) => {
    const margin = 5;
    const minSpaceNeeded = 80;
    const x = rect.left + (rect.width / 2);
    let y = rect.top - margin;
    let placement = 'above';
    
    // If not enough space above, show below the word
    if (rect.top < minSpaceNeeded) {
      y = rect.bottom + margin;
      placement = 'below';
    }
    
    return { x, y, placement };
  };

  const clearTranslations = () => {
    setTranslation(null);
    lastClickedWordRef.current = null;
    setHighlightedWordIndices(new Set());
  };

  // Handle escape key and scroll events
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        clearTranslations();
      }
    };

    const handleScroll = () => {
      clearTranslations();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Render words with translation functionality
  const renderWords = (text) => {
    if (!text || text === 'Loading...') return null;
    
    const words = text.split(/(\s+|[.,!?;:])/);
    
    return (
      <div
        ref={textRef}
        className="text-content-words"
        style={{ lineHeight: 1.8, fontSize: `${pdfTextFontSize}px` }}
      >
        {words.map((word, index) => {
          const trimmedWord = word.trim();
          const isWord = /\w/.test(trimmedWord);
          
          if (!isWord) {
            return <span key={index}>{word}</span>;
          }

          const isHighlighted = highlightedWordIndices.has(index);
          
          return (
            <span
              key={index}
              className={`word ${isHighlighted ? 'translated-word' : ''}`}
              data-word-idx={index}
              onClick={(e) => handleWordClick(trimmedWord, index, e)}
              onMouseDown={() => {
                mouseDownTimeRef.current = Date.now();
                isDraggingRef.current = false;
              }}
              onMouseMove={() => { isDraggingRef.current = true; }}
              style={{ cursor: 'pointer' }}
            >
              {word}
            </span>
          );
        })}
      </div>
    );
  };

  // Get current page text
  const currentPageText = allPagesText[pageNumber - 1] || 'Loading...';

  return (
    <div
      className="pdf-text-reader"
      onClick={clearTranslations}
    >
      {/* Hidden Document component for PDF loading */}
      <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        {pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            options={options}
          />
        ) : (
          <div>No PDF URL provided</div>
        )}
      </div>
      
      {/* Header with page navigation */}
      <Card>
        <CardContent>
          <Box className="pdf-text-header">
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Tooltip title="Zoom out" arrow>
                <IconButton size="small" onClick={handleZoomOut} aria-label="Zoom out">
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom in" arrow>
                <IconButton size="small" onClick={handleZoomIn} aria-label="Zoom in">
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <div className="page-info-navigation" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary" style={{ margin: '0 10px' }}>
                Page {pageNumber} of {numPages || '?'}
              </Typography>
            </div>
            
            <div className="page-controls" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <Button 
                variant="outlined" 
                onClick={previousPage} 
                disabled={pageNumber <= 1}
                size="small"
              >
                ← Previous
              </Button>
              <Button 
                variant="outlined" 
                onClick={nextPage} 
                disabled={pageNumber >= (numPages || 1)}
                size="small"
              >
                Next →
              </Button>
            </div>
          </Box>
        </CardContent>
      </Card>

      {/* Content Area */}
      <Paper
        elevation={1}
        style={{ padding: '1rem', marginTop: '1rem', minHeight: '400px' }}
        onClick={(e) => e.stopPropagation()}
        onMouseUp={handleTextSelection}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: '1rem' }}>
              Loading PDF...
            </Typography>
          </div>
        ) : (
          <div>
            {currentPageText && currentPageText !== 'Loading...' ? (
              renderWords(currentPageText)
            ) : (
              <div>
                <Typography variant="body1" color="textSecondary">
                  {isLoading ? 'Loading PDF text...' : 'No text found on this page. Go to the next.'}
                </Typography>
                {/* Debug info */}
                <div style={{ 
                  marginTop: '1rem', 
                  fontSize: '12px', 
                  color: 'var(--mui-palette-text-secondary, #666)', 
                  background: 'var(--mui-palette-action-hover, rgba(0, 0, 0, 0.04))', 
                  padding: '12px', 
                  borderRadius: '8px',
                  border: '1px solid var(--mui-palette-divider, #e0e0e0)',
                  fontFamily: 'monospace',
                  lineHeight: 1.4
                }}>
                  Debug: PDF URL: {pdfUrl ? 'Set' : 'Not set'} | 
                  Pages: {numPages || 'Unknown'} | 
                  Array length: {allPagesText.length} | 
                  Loading: {isLoading ? 'Yes' : 'No'}
                  <br />
                  PDF URL: {pdfUrl ? pdfUrl.substring(0, 100) + (pdfUrl.length > 100 ? '...' : '') : 'None'}
                </div>
              </div>
            )}
          </div>
        )}
      </Paper>

      {/* Translation Popup */}
      {(translation || isTranslating) && (
        <div
          className="translation-container translation-popup"
          style={{
            position: 'fixed',
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            transform: popupPlacement === 'below' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            zIndex: 1000,
            backgroundColor: 'var(--mui-palette-background-paper, white)',
            color: 'var(--mui-palette-text-primary, #000)',
            border: '1px solid var(--mui-palette-divider, #e0e0e0)',
            borderRadius: '10px',
            padding: '10px 14px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)',
            maxWidth: '220px',
            fontSize: '14px',
            fontWeight: 500,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
        >
          {isTranslating ? (
            <CircularProgress 
              size={16} 
              style={{ 
                margin: '4px auto', 
                display: 'block',
                color: 'var(--mui-palette-primary-main, #1976d2)',
                pointerEvents: 'none'
              }} 
            />
          ) : (
            <div style={{ 
              textAlign: 'center',
              lineHeight: 1.4,
              color: 'var(--mui-palette-text-primary, #000)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              pointerEvents: 'auto'
            }}>
              {translation}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const srcWord = lastClickedWordRef.current || '';
                  if (!srcWord) return;
                  const langMap = { zh: 'zh-CN', pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU', ar: 'ar-SA', hi: 'hi-IN' };
                  const lang = localStorage.getItem('sourceLang') || 'auto';
                  if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); return; }
                  const u = new SpeechSynthesisUtterance(srcWord);
                  if (lang !== 'auto') u.lang = langMap[lang] || lang;
                  window.speechSynthesis.speak(u);
                }}
                sx={{
                  p: 0.5,
                  color: 'primary.main',
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '50%',
                  boxShadow: '0 0 6px 1px rgba(102, 126, 234, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                    boxShadow: '0 0 10px 3px rgba(102, 126, 234, 0.35)',
                  },
                }}
              >
                <VolumeUpIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedPdfReader;
